import { Component, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule, Table } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import { Pagination } from '../../../../services/pagination.abstruct';

import { Subject, takeUntil } from 'rxjs';

import { TranslateService } from '../../../../services/translate.service';
import { UserAdditionalName } from '../../../../models/user.model';
import { MaintainAccountService } from '../maintain-account.service';
import { AdditionalNamesComponent } from '../../create-account/steps/additional-names/additional-names.component';

@Component({
	selector: 'app-tab-additional-name',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		TableModule,
		TagModule,
		TooltipModule,
		ButtonModule,
		ProgressSpinnerModule,
	],
	templateUrl: './tab-additional-name.component.html',
	styleUrls: ['./tab-additional-name.component.scss'],
	providers: [DialogService, MessageService]
})
export class TabAdditionalNameComponent extends Pagination implements OnInit, OnDestroy {
	// Input properties received from the parent component (MaintainAccountComponent)
	@Input() additionalNames!: any[];
	@Input() additionalNamesLoading!: boolean;
	@Input() additionalNamesError!: boolean;

	@Input() customerRecId!: number;

	@ViewChild('dt') dt!: Table;

	ref: DynamicDialogRef | undefined;
	private destroy$ = new Subject<void>();

	globalFilter: string = '';
	globalFilterFields: string[] = [];

	constructor(
		public translateServ: TranslateService,
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private maintainAccountService: MaintainAccountService,
		private dialogService: DialogService
	)
	{
		super();
	}

	ngOnInit(): void {
		if (this.additionalNames?.length > 0) {
			this.globalFilterFields = Object.keys(this.additionalNames[0]);
		}
	}

	ngOnDestroy(): void {
		if (this.ref) {
			this.ref.close();
		}
	}

	getAdditionalNameSeverity(isActiveBoolean: boolean): string {
		return isActiveBoolean ? 'success' : 'danger';
	}

	// source = True will update the Active Status and false the Approved Status.
	updateAdditionalNameStatus(source: boolean, recId: number, newStatus: 0 | 1): void {
		// Calling the API service to update the status
		this.maintainAccountService.updateFunctions(source, this.constructor.name.substring(1), recId, newStatus).subscribe({
			next: (response: any) => {
				// Check if the API response indicates a successful update
				if (response && response[0]?.RecID && response[0].RecID !== -1) {
					const index = this.additionalNames?.findIndex(name => name.RecID === recId);
					if (this.additionalNames && index !== undefined && index > -1) {
						if (source) {
							// Correctly updating the 'IsActive' property
							this.additionalNames[index].IsActive = newStatus;
						}
						else {
							// Correctly updating the 'IsApproved' property
							this.additionalNames[index].IsApproved = newStatus;
						}
						const statusText = newStatus === 1 ? 'Active' : 'Inactive';
						this.messageService.add({
							severity: 'success',
							summary: this.translateServ.dictionary['STATUS_UPDATED'] || 'Status Updated',
							// Correctly using the 'Street' property for the message
							detail: `Additional name of "${this.additionalNames[index].CustomerName}" is now ${statusText}.`,
							life: 3000
						});

					}
				}
				else {
					this.messageService.add({
						severity: 'error',
						summary: this.translateServ.dictionary['ERROR'] || 'Error',
						detail: this.translateServ.dictionary['STATUS_UPDATE_FAILED'] || 'Failed to update status. Record not found or invalid response.',
						life: 5000
					});
				}
			},
			error: (err: any) => {
				console.error('API Error:', err);
				this.messageService.add({
					severity: 'error',
					summary: this.translateServ.dictionary['ERROR'] || 'Error',
					detail: this.translateServ.dictionary['STATUS_UPDATE_FAILED'] || 'Failed to update status. Please try again.',
					life: 5000
				});
			}
		});
	}

	toggleAdditionalNameStatus(additionalName: UserAdditionalName, action: boolean): void {
		console.log("Entered toogle status!");
		console.log(additionalName)

		const newStatus = action ? (additionalName.IsActive === 1 ? 0 : 1) : (additionalName.IsApproved === 1 ? 0 : 1);

		const actionText = newStatus === 1 ?
			(this.translateServ.dictionary['ACTIVATE'] || 'activate') :
			(this.translateServ.dictionary['DEACTIVATE'] || 'deactivate');

		this.confirmationService.confirm({
			message: `${this.translateServ.dictionary['CONFIRM_CHANGE_STATUS'] || 'Are you sure you want to'} ${actionText} ${additionalName.CustomerName}?`,
			header: this.translateServ.dictionary['CONFIRM_ACTION'] || 'Confirm Action',
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: this.translateServ.dictionary['YES'] || 'Yes',
			rejectLabel: this.translateServ.dictionary['NO'] || 'No',
			accept: () => {
				this.updateAdditionalNameStatus(action, additionalName.RecID, newStatus);
			},
			reject: () => {
				this.messageService.add({
					severity: 'info',
					summary: this.translateServ.dictionary['CANCELLED'] || 'Cancelled',
					detail: this.translateServ.dictionary['ACTION_CANCELLED'] || 'Action cancelled.',
					life: 3000
				});
			}
		});
	}

	createNewRecord(): void {
		// FIX: Pass the customerRecId to the AdditionalNamesComponent via the data property
		this.ref = this.dialogService.open(AdditionalNamesComponent, {
			header: this.translateServ.dictionary['STEPPER_DIALOG_TITLE'] || 'Create New Record',
			width: '70%',
			contentStyle: { overflow: 'auto' },
			baseZIndex: 10000,
			maximizable: true,
			data: {
				isDialog: true,
				isCreate: true,
				customerRecId: this.customerRecId // Pass the ID here
			}
		});

		// Subscribe to the dialog's onClose event
		this.ref.onClose.pipe(takeUntil(this.destroy$)).subscribe((result) => {
			// Check if the dialog was closed with a successful action (e.g., a "save" button click)
			// The AdditionalNamesComponent would need to return a value like 'true' upon success
			// to trigger this.
			if (result) {
				this.messageService.add({
					severity: 'success',
					summary: this.translateServ.dictionary['SUCCESS'] || 'Success',
					detail: this.translateServ.dictionary['MAINT_CREATE_SUCCESS_MSG'] || 'Record created successfully.',
					life: 3000
				});
				this.reloadAdditionalNamesForUser();
			}
		});
	}

	// Confirms and deletes a record
	confirmDelete(additionalName: UserAdditionalName): void {
		this.confirmationService.confirm({
			message: `${this.translateServ.dictionary['MAINT_DELETE_CONFIRM_MSG'] || 'Are you sure you want to delete this record?'} (RecID: ${additionalName.RecID})`,
			header: this.translateServ.dictionary['MAINT_DELETE_CONFIRM_HEADER'] || 'Confirm Deletion',
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: this.translateServ.dictionary['CONFIRM_DELETE'] || 'Delete',
			rejectLabel: this.translateServ.dictionary['CONFIRM_CANCEL'] || 'Cancel',
			accept: () => {
				this.maintainAccountService.deleteRecord(this.constructor.name.substring(1), additionalName.RecID)
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: (response) => {
							this.messageService.add({
								severity: 'success',
								summary: this.translateServ.dictionary['SUCCESS'] || 'Success',
								detail: this.translateServ.dictionary['MAINT_DELETE_SUCCESS_MSG'] || 'Record deleted successfully.',
								life: 3000,
							});
							this.reloadAdditionalNamesForUser();
						},
						error: (error) => {
							console.error('Failed to delete record:', error);
							this.messageService.add({
								severity: 'error',
								summary: this.translateServ.dictionary['ERROR'] || 'Error',
								detail: this.translateServ.dictionary['MAINT_DELETE_FAILED_MSG'] || 'Failed to delete record.',
								life: 5000,
							});
						}
					});
			},
			reject: () => {
				// The user clicked "Cancel", so we can just close the dialog.
			}
		});
	}

	/**
	 * Opens the dialog to edit an existing additional name record.
	 * @param additionalName The record to be edited.
	 */
	editAdditionalName(additionalName: UserAdditionalName): void {
		this.ref = this.dialogService.open(AdditionalNamesComponent, {
			header: this.translateServ.dictionary['STEPPER_DIALOG_TITLE_EDIT'] || 'Edit Record',
			width: '70%',
			contentStyle: { overflow: 'auto' },
			baseZIndex: 10000,
			maximizable: true,
			data: {
				isDialog: true,
				isCreate: false,
				customerRecId: this.customerRecId,
				nameEntry: {
					id: additionalName.RecID.toString(),
					recId: additionalName.RecID,
					name: additionalName.CustomerName,
					description: additionalName.Descr,
					isActive: additionalName.IsActive,
					isApproved: additionalName.IsApproved,
					customerRecId: additionalName.RecID
				}
			}
		});

		this.ref.onClose.pipe(takeUntil(this.destroy$)).subscribe((result) => {
			if (result) {
				this.messageService.add({
					severity: 'success',
					summary: this.translateServ.dictionary['SUCCESS'] || 'Success',
					detail: this.translateServ.dictionary['MAINT_UPDATE_SUCCESS_MSG'] || 'Record updated successfully.',
					life: 3000
				});
				this.reloadAdditionalNamesForUser();
			}
		});
	}

	// Reloads the data for the additional names table
	private reloadAdditionalNamesForUser(): void {
		// Call the service method to get the updated data
		const index = this.maintainAccountService.getMappingIndex("additionalNames");
		const instance = this.maintainAccountService.getUserDataMappings();
		this.maintainAccountService.getDatas(this.customerRecId, instance[index].apiName, instance[index].special)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (data) => {
					// This is the key change: update the existing array in place
					// to preserve PrimeNG's sorting and filtering state.
					this.additionalNames.splice(0, this.additionalNames.length, ...data);
					this.additionalNamesLoading = false;
				},
				error: (error) => {
					console.error('Failed to reload additional names:', error);
					this.additionalNamesError = true;
					this.additionalNamesLoading = false;
					this.messageService.add({
						severity: 'error',
						summary: this.translateServ.dictionary['ERROR'] || 'Error',
						detail: this.translateServ.dictionary['MAINT_LOAD_FAILED_MSG_ADDI'] || 'Failed to load additional names.',
						life: 5000
					});
				}
			});
	}

	applyGlobalFilter(): void {
		this.dt.filterGlobal(this.globalFilter, 'contains');
	}
}
